import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './MembersPage.css';

const normalizeGroupIds = (values = []) => (
  [...new Set((Array.isArray(values) ? values : []).map(Number).filter((value) => Number.isInteger(value) && value > 0))]
    .sort((a, b) => a - b)
);

const groupIdSignature = (values = []) => JSON.stringify(normalizeGroupIds(values));

const buildMemberGroupDraft = (members = []) => members.reduce((acc, member) => {
  acc[member.memberId] = normalizeGroupIds((member.groups || []).map((group) => group.id));
  return acc;
}, {});

const MembersPage = () => {
  const { selectedCompany, user, permissions } = useAuth();
  const companyId = selectedCompany?.id ?? null;
  const canManageMembers = Boolean(permissions?.canViewMembers || permissions?.canManageMembers);
  const isSuperAdmin = Boolean(user?.isAdmin);
  const canEditMembers = Boolean(permissions?.canManageMembers || permissions?.isOwner || isSuperAdmin);
  const canRemoveMembers = Boolean(permissions?.isOwner || isSuperAdmin);
  const canTransferOwnership = Boolean(permissions?.isOwner || isSuperAdmin);
  const currentAccountId = user?.accountId || user?.userId || null;

  const [members, setMembers] = useState([]);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [, setError] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [, setInviteError] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [, setInviteSuccess] = useState(null);

  const loadMembers = useCallback(async () => {
    if (!companyId || !canManageMembers) {
      setMembers([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await DashboardApi.getCompanyMembers(companyId);
      setMembers(Array.isArray(response?.members) ? response.members : []);
      setPermissionGroups(Array.isArray(response?.permissionGroups) ? response.permissionGroups : []);
    } catch (err) {
      const message = err.message || 'Kunne ikke hente medlemmene.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente medlemmer', source: 'Medlemmer' });
    } finally {
      setLoading(false);
    }
  }, [companyId, canManageMembers]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!companyId) return;
    const trimmed = inviteEmail.trim();
    if (!trimmed) {
      setInviteError('Skriv inn e-postadressen du vil invitere.');
      notifyError('Skriv inn e-postadressen du vil invitere.', { source: 'Medlemmer' });
      return;
    }
    setInviteLoading(true);
    setInviteError(null);
    setInviteSuccess(null);
    try {
      const result = await DashboardApi.inviteCompanyMember(companyId, { email: trimmed });
      setInviteEmail('');
      
      // Show different success messages based on whether user is new or existing
      if (result.isNewUser) {
        const message = `En invitasjon er sendt til ${trimmed}. De vil motta en e-post med instruksjoner.`;
        setInviteSuccess(message);
        notifySuccess(message, { source: 'Medlemmer' });
      } else {
        const message = `${trimmed} er nå lagt til som medlem i selskapet.`;
        setInviteSuccess(message);
        notifySuccess(message, { source: 'Medlemmer' });
        await loadMembers();
      }
    } catch (err) {
      const message = err.message || 'Kunne ikke invitere medlem.';
      setInviteError(message);
      notifyError(message, { title: 'Kunne ikke invitere medlem', source: 'Medlemmer' });
    } finally {
      setInviteLoading(false);
    }
  };

  const [removingMember, setRemovingMember] = useState(null); // { memberId, displayName, email }
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeError, setRemoveError] = useState(null);
  const [savingRolesFor, setSavingRolesFor] = useState(null);
  const [draftGroupsByMember, setDraftGroupsByMember] = useState({});
  const [openGroupMenuFor, setOpenGroupMenuFor] = useState(null);
  const [transferMember, setTransferMember] = useState(null);
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferError, setTransferError] = useState(null);

  const sortedMembers = useMemo(
    () => members
      .filter((member) => member.status !== 'removed')
      .slice()
      .sort((a, b) => {
      if (a.isOwner && !b.isOwner) return -1;
      if (!a.isOwner && b.isOwner) return 1;
      return (a.displayName || '').localeCompare(b.displayName || '');
    }),
    [members],
  );

  useEffect(() => {
    setDraftGroupsByMember(buildMemberGroupDraft(members));
    setOpenGroupMenuFor(null);
  }, [members]);

  const dirtyRoleMemberIds = useMemo(() => sortedMembers
    .filter((member) => !member.isOwner)
    .filter((member) => {
      const original = (member.groups || []).map((group) => group.id);
      const draft = draftGroupsByMember[member.memberId] || [];
      return groupIdSignature(original) !== groupIdSignature(draft);
    })
    .map((member) => member.memberId), [draftGroupsByMember, sortedMembers]);

  const hasUnsavedRoleChanges = Boolean(canEditMembers && dirtyRoleMemberIds.length > 0);

  if (!companyId) {
    return (
      <div className="page">
        <div className="card">
          <h1>Medlemmer</h1>
          <p>Velg et selskap for å administrere medlemmene.</p>
        </div>
      </div>
    );
  }

  if (!canManageMembers) {
    return (
      <div className="page">
        <div className="card">
          <h1>Medlemmer</h1>
          <p>Du må være eier av selskapet for å se eller endre medlemmene.</p>
        </div>
      </div>
    );
  }

  const handleRemoveMember = async () => {
    if (!companyId || !removingMember || !canRemoveMembers) return;
    setRemoveLoading(true);
    setRemoveError(null);
    try {
      await DashboardApi.removeCompanyMember(companyId, removingMember.memberId);
      setRemovingMember(null);
      await loadMembers();
      notifySuccess('Medlemmet ble fjernet.', { source: 'Medlemmer' });
    } catch (err) {
      const message = err.message || 'Kunne ikke fjerne medlemmet.';
      setRemoveError(message);
      notifyError(message, { title: 'Kunne ikke fjerne medlem', source: 'Medlemmer' });
    } finally {
      setRemoveLoading(false);
    }
  };

  const addDraftGroup = (member, groupId) => {
    if (!canEditMembers || member.isOwner) return;
    setDraftGroupsByMember((current) => {
      const ids = new Set(current[member.memberId] || []);
      ids.add(Number(groupId));
      return { ...current, [member.memberId]: normalizeGroupIds([...ids]) };
    });
    setOpenGroupMenuFor(null);
  };

  const removeDraftGroup = (member, groupId) => {
    if (!canEditMembers || member.isOwner) return;
    setDraftGroupsByMember((current) => {
      const ids = new Set(current[member.memberId] || []);
      ids.delete(Number(groupId));
      return { ...current, [member.memberId]: normalizeGroupIds([...ids]) };
    });
  };

  const saveRoleChanges = async () => {
    if (!companyId || !canEditMembers || dirtyRoleMemberIds.length === 0) return;
    setSavingRolesFor('roles');
    try {
      let latestResponse = null;
      for (const memberId of dirtyRoleMemberIds) {
        latestResponse = await DashboardApi.updateCompanyMemberGroups(companyId, memberId, draftGroupsByMember[memberId] || []);
      }
      if (latestResponse) {
        setMembers(Array.isArray(latestResponse?.members) ? latestResponse.members : []);
        setPermissionGroups(Array.isArray(latestResponse?.permissionGroups) ? latestResponse.permissionGroups : permissionGroups);
      }
      notifySuccess('Roller ble oppdatert.', { source: 'Medlemmer' });
    } catch (err) {
      notifyError(err.message || 'Kunne ikke oppdatere roller.', { title: 'Kunne ikke oppdatere roller', source: 'Medlemmer' });
      await loadMembers();
    } finally {
      setSavingRolesFor(null);
    }
  };

  const discardRoleChanges = () => {
    setDraftGroupsByMember(buildMemberGroupDraft(members));
    setOpenGroupMenuFor(null);
  };

  const handleTransferOwnership = async () => {
    if (!companyId || !transferMember || !canTransferOwnership) return;
    setTransferLoading(true);
    setTransferError(null);
    try {
      const response = await DashboardApi.transferCompanyOwnership(companyId, transferMember.memberId);
      setMembers(Array.isArray(response?.members) ? response.members : []);
      setPermissionGroups(Array.isArray(response?.permissionGroups) ? response.permissionGroups : permissionGroups);
      setTransferMember(null);
      notifySuccess('Eierskap ble overført.', { source: 'Medlemmer' });
    } catch (err) {
      const message = err.message || 'Kunne ikke overføre eierskap.';
      setTransferError(message);
      notifyError(message, { title: 'Kunne ikke overføre eierskap', source: 'Medlemmer' });
    } finally {
      setTransferLoading(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <p className="page-eyebrow">Team</p>
          <h1>Medlemmer</h1>
          <p className="page-subtitle">Administrer hvem som har tilgang til {selectedCompany?.name || 'selskapet'}.</p>
        </div>
      </div>
      <div className={`card members-list-card${openGroupMenuFor ? ' members-list-card--popover-active' : ''}`}>
        <div className="card-header">
          <div>
            <h2>Selskapsmedlemmer</h2>
            <p className="text-muted">Du har {sortedMembers.length} medlem{sortedMembers.length === 1 ? '' : 'mer'} i selskapet.</p>
          </div>
        </div>
        {loading ? (
          <p>Laster medlemmer…</p>
        ) : sortedMembers.length === 0 ? (
          <p>Ingen medlemmer er lagt til enda.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Navn</th>
                <th>E-post</th>
                <th>Roller</th>
                <th>Status</th>
                <th>Handlinger</th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.map((member) => (
                <tr key={member.memberId}>
                  <td>{member.displayName || member.email}</td>
                  <td>{member.email}</td>
                  <td>
                    {member.isOwner ? (
                      <div className="member-role-pills" aria-label="Eierrollen er låst">
                        <span className="member-role-pill member-role-pill--owner">
                          Eier
                        </span>
                      </div>
                    ) : (
                      <div className="member-role-editor">
                        <div className="member-role-pills">
                          {(() => {
                            const selectedIds = draftGroupsByMember[member.memberId] || [];
                            const selectedGroups = permissionGroups.filter((group) => selectedIds.includes(Number(group.id)));
                            const availableGroups = permissionGroups.filter((group) => !selectedIds.includes(Number(group.id)));
                            return (
                              <>
                                {selectedGroups.map((group) => (
                                  <span key={group.id} className="member-role-pill">
                                    {group.name}
                                    {canEditMembers && (
                                      <button
                                        type="button"
                                        className="member-role-pill-remove"
                                        onClick={() => removeDraftGroup(member, group.id)}
                                        aria-label={`Fjern ${group.name}`}
                                        disabled={savingRolesFor === 'roles'}
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                          <path d="M3 6h18" />
                                          <path d="M8 6V4h8v2" />
                                          <path d="M19 6l-1 14H6L5 6" />
                                          <path d="M10 11v5" />
                                          <path d="M14 11v5" />
                                        </svg>
                                      </button>
                                    )}
                                  </span>
                                ))}
                                {selectedGroups.length === 0 && permissionGroups.length > 0 && (
                                  <span className="member-role-empty">Ingen roller</span>
                                )}
                                {canEditMembers && (
                                  <span className="member-role-add-wrap">
                                    <button
                                      type="button"
                                      className="member-role-pill member-role-pill--add"
                                      onClick={() => setOpenGroupMenuFor((current) => (
                                        current === member.memberId ? null : member.memberId
                                      ))}
                                      disabled={savingRolesFor === 'roles' || availableGroups.length === 0}
                                      aria-expanded={openGroupMenuFor === member.memberId}
                                      aria-label={`Legg til rolle for ${member.displayName || member.email}`}
                                      title={availableGroups.length === 0 ? 'Ingen flere roller å legge til' : 'Legg til rolle'}
                                    >
                                      +
                                    </button>
                                    {openGroupMenuFor === member.memberId && availableGroups.length > 0 && (
                                      <div className="member-role-menu" role="menu">
                                        {availableGroups.map((group) => (
                                          <button
                                            key={group.id}
                                            type="button"
                                            onClick={() => addDraftGroup(member, group.id)}
                                          >
                                            {group.name}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    )}
                    {!member.isOwner && permissionGroups.length === 0 && (
                      <span className="text-muted">Ingen roller er opprettet</span>
                    )}
                  </td>
                  <td className="text-muted">{member.status}</td>
                  <td>
                    <div className="member-actions">
                      {canTransferOwnership && !member.isOwner && member.status === 'active' && (
                        <button
                          type="button"
                          className="button button-secondary button-small"
                          onClick={() => {
                            setTransferError(null);
                            setTransferMember({
                              memberId: member.memberId,
                              displayName: member.displayName,
                              email: member.email,
                            });
                          }}
                        >
                          Overfør eierskap
                        </button>
                      )}
                      {canRemoveMembers && (isSuperAdmin || member.accountId !== currentAccountId) && member.status === 'active' && (
                        <button
                          type="button"
                          className="button button-danger-outline button-small"
                          onClick={() => setRemovingMember({
                            memberId: member.memberId,
                            displayName: member.displayName,
                            email: member.email,
                          })}
                        >
                          Fjern
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {permissions?.canManageMembers && (
        <div className="card">
          <h2>Inviter nytt medlem</h2>
          <form className="form-inline" onSubmit={handleInvite}>
            <label htmlFor="inviteEmail">
              E-postadresse
              <input
                id="inviteEmail"
                type="email"
                className="input"
                placeholder="fornavn@bedrift.no"
                value={inviteEmail}
                onChange={(event) => {
                  setInviteEmail(event.target.value);
                  setInviteSuccess(null);
                }}
              />
            </label>
            <div className="form-actions">
              <button type="submit" className="button button-primary" disabled={inviteLoading}>
                {inviteLoading ? 'Sender invitasjon…' : 'Inviter medlem'}
              </button>
            </div>
          </form>
          <p className="text-muted" style={{ marginTop: '12px', fontSize: '0.875rem' }}>
            Nye medlemmer får ingen systemtilgang før de legges i en tilgangsgruppe.
          </p>
        </div>
      )}

      {hasUnsavedRoleChanges && (
        <div className="members-save-prompt" role="status" aria-live="polite">
          <div>
            <strong>Ulagrede rolleendringer</strong>
            <p>Endringene forkastes hvis du forlater siden.</p>
          </div>
          <div className="members-save-prompt-actions">
            <button
              type="button"
              className="button button-secondary button-small"
              onClick={discardRoleChanges}
              disabled={savingRolesFor === 'roles'}
            >
              Forkast
            </button>
            <button
              type="button"
              className="button button-primary button-small"
              onClick={saveRoleChanges}
              disabled={savingRolesFor === 'roles'}
            >
              {savingRolesFor === 'roles' ? 'Lagrer...' : 'Lagre endringer'}
            </button>
          </div>
        </div>
      )}

      {transferMember && (
        <div
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !transferLoading && setTransferMember(null)}
        >
          <div
            className="modal-content card"
            style={{
              maxWidth: '480px',
              width: '90%',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Overføre eierskap?</h2>
            <p>
              <strong>{transferMember.displayName || transferMember.email}</strong>{' '}
              blir ny eier av selskapet. Eksisterende eierrolle flyttes til det valgte medlemmet.
            </p>
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              Eierrollen gir full tilgang og kan ikke begrenses av roller.
            </p>
            {transferError && (
              <div className="auth-error" style={{ marginBottom: '16px' }}>
                ⚠️ {transferError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => setTransferMember(null)}
                disabled={transferLoading}
                style={{ flex: 1 }}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="button button-primary"
                onClick={handleTransferOwnership}
                disabled={transferLoading}
                style={{ flex: 1 }}
              >
                {transferLoading ? 'Overfører...' : 'Overfør eierskap'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove Member Confirmation Modal */}
      {removingMember && (
        <div 
          className="modal-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => !removeLoading && setRemovingMember(null)}
        >
          <div 
            className="modal-content card"
            style={{
              maxWidth: '440px',
              width: '90%',
              padding: '24px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginTop: 0 }}>Fjern medlem?</h2>
            <p>
              Er du sikker på at du vil fjerne{' '}
              <strong>{removingMember.displayName || removingMember.email}</strong>{' '}
              fra selskapet?
            </p>
            <p className="text-muted" style={{ fontSize: '0.875rem' }}>
              Medlemmet vil miste tilgang til selskapet og alle tilhørende ressurser.
            </p>
            {removeError && (
              <div className="auth-error" style={{ marginBottom: '16px' }}>
                ⚠️ {removeError}
              </div>
            )}
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                type="button"
                className="button button-secondary"
                onClick={() => setRemovingMember(null)}
                disabled={removeLoading}
                style={{ flex: 1 }}
              >
                Avbryt
              </button>
              <button
                type="button"
                className="button button-danger"
                onClick={handleRemoveMember}
                disabled={removeLoading}
                style={{ flex: 1 }}
              >
                {removeLoading ? 'Fjerner...' : 'Ja, fjern medlem'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembersPage;
